import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import {
  ComedianNotificationPreferences,
  ComedianNotificationPreferenceCreatePayload,
  ComedianNotificationPreferenceUpdatePayload,
} from "../../models/comedian-notification-preferences";
import { logException } from "@punchup/sentry";

/**
 * Get all comedian-specific notification preferences for the authenticated user
 */
export async function getComedianNotificationPreferences(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<ComedianNotificationPreferences[]> {
  const { data, error } = await supabase
    .from("user_comedian_notification_preferences")
    .select(
      "comedian_id, new_free_content, new_subscription_content, new_tour_dates, push_notifications"
    )
    .eq("user_id", userId);

  if (error) {
    logException(error, {
      data: "Database error while fetching comedian notification preferences",
    });
    throw error;
  }

  return data;
}

/**
 * Get notification preference for a specific comedian for the authenticated user
 */
export async function getComedianNotificationPreference(
  supabase: SupabaseClient<Database>,
  userId: string,
  comedianId: string
): Promise<ComedianNotificationPreferences | null> {
  const { data, error } = await supabase
    .from("user_comedian_notification_preferences")
    .select(
      "comedian_id, new_free_content, new_subscription_content, new_tour_dates, push_notifications"
    )
    .eq("user_id", userId)
    .eq("comedian_id", comedianId)
    .maybeSingle();

  if (error) {
    logException(error, {
      data: "Database error while fetching comedian notification preference",
    });
    throw error;
  }

  return data;
}

/**
 * Create a new comedian-specific notification preference override
 */
export async function createComedianNotificationPreference(
  supabase: SupabaseClient<Database>,
  userId: string,
  payload: ComedianNotificationPreferenceCreatePayload
): Promise<ComedianNotificationPreferences> {
  const { data, error } = await supabase
    .from("user_comedian_notification_preferences")
    .insert({
      user_id: userId,
      comedian_id: payload.comedian_id,
      new_free_content: payload.new_free_content,
      new_subscription_content: payload.new_subscription_content,
      new_tour_dates: payload.new_tour_dates,
    })
    .select(
      "comedian_id, new_free_content, new_subscription_content, new_tour_dates, push_notifications"
    )
    .single();

  if (error) {
    logException(error, {
      data: "Database error while creating comedian notification preference",
    });
    throw error;
  }

  return data;
}

/**
 * Update an existing comedian-specific notification preference override
 */
export async function updateComedianNotificationPreference(
  supabase: SupabaseClient<Database>,
  userId: string,
  payload: ComedianNotificationPreferenceUpdatePayload
): Promise<ComedianNotificationPreferences> {
  const updatePayload: Record<string, boolean | null> = {};
  if (payload.new_free_content !== undefined) {
    updatePayload.new_free_content = payload.new_free_content;
  }
  if (payload.new_subscription_content !== undefined) {
    updatePayload.new_subscription_content = payload.new_subscription_content;
  }
  if (payload.new_tour_dates !== undefined) {
    updatePayload.new_tour_dates = payload.new_tour_dates;
  }

  const { data, error } = await supabase
    .from("user_comedian_notification_preferences")
    .update(updatePayload)
    .eq("user_id", userId)
    .eq("comedian_id", payload.comedian_id)
    .select(
      "comedian_id, new_free_content, new_subscription_content, new_tour_dates, push_notifications"
    )
    .single();

  if (error) {
    logException(error, {
      data: "Database error while updating comedian notification preference",
    });
    throw error;
  }

  return data;
}

/**
 * Delete a comedian-specific notification preference override
 */
export async function deleteComedianNotificationPreference(
  supabase: SupabaseClient<Database>,
  userId: string,
  comedianId: string
): Promise<boolean> {
  const { error } = await supabase
    .from("user_comedian_notification_preferences")
    .delete()
    .eq("user_id", userId)
    .eq("comedian_id", comedianId);

  if (error) {
    logException(error, {
      data: "Database error while deleting comedian notification preference",
    });
    throw error;
  }

  return true;
}
